"use client";

import { useEffect } from "react";
import { useParams } from "next/navigation";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { useCookieConsent } from "@leabankab/component-library/cookie-consent/react";
import {
	type RuntimeConfig,
	useRuntimeConfig,
} from "@/contexts/runtime-config";
import { DD_SERVICE, DD_SITE, DD_VERSION } from "@/datadog/config";
import { setLoggingApplicationId } from "@/datadog/logger";

/**
 * Initialisation is deliberately lazy: RUM and browser logs are analytics
 * cookies, so nothing may load before the visitor consents. The SDKs cannot
 * be torn down, so revoking consent flips tracking consent to `not-granted`.
 */
const initDatadogBrowser = ({
	env,
	applicationId,
	clientToken,
}: NonNullable<RuntimeConfig["datadog"]>) => {
	if (!datadogRum.getInitConfiguration()) {
		datadogRum.init({
			applicationId,
			clientToken,
			site: DD_SITE,
			service: DD_SERVICE,
			env,
			version: DD_VERSION,
			sessionSampleRate: 100,
			sessionReplaySampleRate: 0,
			trackUserInteractions: true,
			trackResources: true,
			trackLongTasks: true,
			defaultPrivacyLevel: "mask-user-input",
			trackingConsent: "granted",
		});
	}

	if (!datadogLogs.getInitConfiguration()) {
		datadogLogs.init({
			clientToken,
			site: DD_SITE,
			service: DD_SERVICE,
			env,
			version: DD_VERSION,
			forwardErrorsToLogs: true,
			forwardConsoleLogs: "all",
			sessionSampleRate: 100,
			trackingConsent: "granted",
		});
	}
};

export default function DatadogInit() {
	const { applicationId } = useParams<{ applicationId?: string }>();
	const { consent } = useCookieConsent();
	const hasAnalyticsConsent = consent?.analytics === true;
	const { datadog } = useRuntimeConfig();

	useEffect(() => {
		if (!datadog) return;

		if (hasAnalyticsConsent) {
			initDatadogBrowser(datadog);
			datadogRum.setTrackingConsent("granted");
			datadogLogs.setTrackingConsent("granted");
			return;
		}

		if (datadogRum.getInitConfiguration()) {
			datadogRum.setTrackingConsent("not-granted");
		}
		if (datadogLogs.getInitConfiguration()) {
			datadogLogs.setTrackingConsent("not-granted");
		}
	}, [datadog, hasAnalyticsConsent]);

	useEffect(() => {
		if (!applicationId || !datadog || !hasAnalyticsConsent) return;

		setLoggingApplicationId(applicationId);
		datadogRum.setGlobalContextProperty("applicationId", applicationId);
	}, [applicationId, datadog, hasAnalyticsConsent]);

	return null;
}
